import { useId, useState, type ReactNode } from 'react';

/**
 * An item's tags, and the field to change them (M4.2.3).
 *
 * Chips for what is there, each removable, and one field that adds a tag on
 * Enter or comma. Autocomplete is the browser's own `<datalist>` over the tags
 * already in use: it is keyboard-native, costs nothing, and nudges "Research"
 * and "research" towards one tag, which is most of what autocomplete is for
 * here.
 *
 * Every change is sent as the whole set — the API replaces rather than adds —
 * so a quick sequence of edits cannot interleave into a set nobody chose.
 */

export interface TagEditorProps {
  readonly tags: readonly string[];
  /** Tags used anywhere in the account, for suggestions. */
  readonly known: readonly string[];
  readonly onChange: (tags: readonly string[]) => void;
}

export function TagEditor(props: TagEditorProps): ReactNode {
  const [draft, setDraft] = useState('');
  const listId = useId();

  const add = (raw: string): void => {
    const tag = raw.trim().replace(/\s+/g, ' ');
    setDraft('');
    if (tag === '') return;
    if (
      props.tags.some(
        (existing) => existing.toLocaleLowerCase() === tag.toLocaleLowerCase(),
      )
    ) {
      return;
    }
    props.onChange([...props.tags, tag]);
  };

  const suggestions = props.known.filter(
    (name) =>
      !props.tags.some((tag) => tag.toLocaleLowerCase() === name.toLocaleLowerCase()),
  );

  return (
    <div className="tags" aria-label="Tags">
      {props.tags.map((tag) => (
        <span className="tags__chip" key={tag}>
          {tag}
          <button
            className="tags__remove"
            aria-label={`Remove tag ${tag}`}
            onClick={() => {
              props.onChange(props.tags.filter((existing) => existing !== tag));
            }}
          >
            ×
          </button>
        </span>
      ))}
      <input
        className="tags__input"
        aria-label="Add a tag"
        placeholder={props.tags.length === 0 ? 'Add a tag' : 'Add another'}
        list={listId}
        maxLength={100}
        value={draft}
        onChange={(event) => {
          const value = event.target.value;
          if (value.endsWith(',')) {
            add(value.slice(0, -1));
          } else {
            setDraft(value);
          }
        }}
        onKeyDown={(event) => {
          // Keys typed here are text, not reader shortcuts.
          event.stopPropagation();
          if (event.key === 'Enter') {
            event.preventDefault();
            add(draft);
          } else if (event.key === 'Backspace' && draft === '' && props.tags.length > 0) {
            props.onChange(props.tags.slice(0, -1));
          }
        }}
      />
      <datalist id={listId}>
        {suggestions.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
    </div>
  );
}
